// Workspace draft-mode state machine (research + build).
//
// Encapsulates the lazy-create flow that powers two entry points:
//   - the sidebar "Regulatory Research" / "Build & Artifacts" buttons, which
//     drop the user into an EMPTY workspace with no project yet (topic-less
//     blank draft); and
//   - the Build topic-box presses, which seed the draft with a topic.
// In every case no backend project is created until the user sends their first
// chat message; `trySendDraft` performs the lazy-create then routes the URL to
// the freshly-minted project.
//
// State, refs, reset effects, and the three actions App.tsx invokes from event
// handlers (`beginBlankDraft` for sidebar entry, `beginDraft` for Build
// topic-box presses, `trySendDraft` for chat-panel sends).
//
// This file is intentionally extracted from App.tsx so it gets full
// TypeScript coverage. App.tsx still has a file-level `@ts-nocheck`
// pragma (legacy 5K-line file with cascading errors out of scope for
// this PR), so we keep the new state-machine logic here where the
// compiler actually checks it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../stores/projectStore';
import type { TopicGroup } from '../components/landing/BuildShowcase';
import { deriveProjectName } from '../components/projects/NewProjectForm';
import type { WorkspaceMode } from '../types/workspace';
import type { MainView } from '../router/routes';

// BAR-40: navigation is URL-driven now; the shell passes its `navigateToView`
// writer here. Use the canonical MainView union so this stays in sync with the
// router instead of drifting (it previously omitted 'admin').
type NavigateToView = (view: MainView) => void;

/** Which kind of workspace the draft will become once a project is created. */
export type DraftType = 'research' | 'build';

/** The transient draft the user is composing against before any project
 *  exists. `topic` is set only for Build topic-box presses (drives the
 *  plan-prompt heading); sidebar blank drafts have `topic: null`. */
export interface WorkspaceDraft {
    type: DraftType;
    topic: TopicGroup | null;
}

interface UseWorkspaceDraftModeArgs {
    /** Current top-level view. The hook watches this to clear draft
     *  state when the user transitions out of a workspace view. */
    routeView: string;
    /** Active project id (or null if none). The hook watches this to
     *  clear draft state when the user opens a different project from
     *  the project library while staying in workspace view. */
    activeProjectId: string | null | undefined;
    /** Logged-in user. Mirrored into a ref so the lazy-create handler
     *  reads a live value rather than a stale closure capture. */
    userData: { email?: string | null } | null | undefined;
    navigateToView: NavigateToView;
    setWorkspaceMode: (mode: WorkspaceMode, initiator?: 'user' | 'agent') => void;
    setPrefillChatInput: (value: string | null) => void;
    setAutoSendPrefill: (value: boolean) => void;
    /** Project-store helpers passed in so the hook stays decoupled
     *  from the App-level imports of the same store. */
    clearActiveProject: () => Promise<boolean>;
    /** App-defined helper that scope-resets in-flight chat-loading
     *  state. Pass a projectId to restore loading state for that
     *  project; call with no args to mint a fresh empty chat (used when
     *  entering a blank draft, before any project exists). */
    resetChatState: (projectId?: string) => void;
}

export interface UseWorkspaceDraftModeResult {
    /** The draft the user is currently composing against (null when not
     *  in draft mode). `pendingDraft.type` selects research vs build;
     *  `pendingDraft.topic?.prompt` (Build box presses only) drives the
     *  plan-prompt heading at render time. */
    pendingDraft: WorkspaceDraft | null;
    /** User-facing error message when a lazy-create fails. Cleared on
     *  retry / new draft / workspace exit. */
    draftCreateError: string | null;
    /** Increments on each lazy-create failure. Use as part of the
     *  GeminiChatArea key to force a remount that re-applies the
     *  pre-armed prefillChatInput as `initialInput`, restoring the
     *  user's typed message in the composer. */
    draftRetryAttempt: number;
    /** Called from the sidebar "Regulatory Research" / "Build & Artifacts"
     *  buttons. Enters a topic-less blank draft and navigates to the empty
     *  workspace (research → /research/new, build → /build/new). No backend
     *  project is created here. */
    beginBlankDraft: (type: DraftType) => Promise<void>;
    /** Called from `<BuildShowcase onBoxDraft>`. Captures the topic in
     *  transient state (always a Build draft) and navigates to the plan
     *  workspace in draft mode. No backend project is created here. */
    beginDraft: (topic: TopicGroup) => Promise<void>;
    /** Called from the chat-panel `onSendMessage` interceptor. If the
     *  user is in draft mode and there's no active project, this
     *  triggers the lazy-create and returns `{ handled: true }` so the
     *  caller skips its default forward-to-store behavior. Otherwise
     *  returns `{ handled: false }`. */
    trySendDraft: (msg: string) => { handled: boolean };
}

/**
 * useWorkspaceDraftMode owns the draft-and-lazy-create state machine for both
 * the research and build workspaces. Exposes the React state needed for
 * rendering plus three actions for the event handlers in App.tsx.
 *
 * Race-window mitigations:
 * - `pendingDraftRef` mirrors `pendingDraft` and is updated
 *   synchronously alongside every state setter, so the lazy-create
 *   guard can read live state without waiting on React commit.
 * - `userDataRef` mirrors the `userData` arg, so the create handler
 *   reads a live email rather than a stale closure value.
 * - `draftGenerationRef` increments on each new draft (or reset).
 *   The lazy-create captures the generation at call time and skips
 *   user-visible state updates in `.then`/`.catch` if the user has
 *   moved on (pressed a different box, navigated away, etc.).
 * - `useProjectStore.getState().activeProject` is read live (not
 *   from React state) so the guard isn't fooled by stale closures.
 */
export function useWorkspaceDraftMode(args: UseWorkspaceDraftModeArgs): UseWorkspaceDraftModeResult {
    const {
        routeView,
        activeProjectId,
        userData,
        navigateToView,
        setWorkspaceMode,
        setPrefillChatInput,
        setAutoSendPrefill,
        clearActiveProject,
        resetChatState,
    } = args;

    const [pendingDraft, setPendingDraft] = useState<WorkspaceDraft | null>(null);
    const [draftCreateError, setDraftCreateError] = useState<string | null>(null);
    const [draftRetryAttempt, setDraftRetryAttempt] = useState<number>(0);

    const pendingDraftRef = useRef<WorkspaceDraft | null>(null);
    const draftCreateInFlightRef = useRef<boolean>(false);
    const draftGenerationRef = useRef<number>(0);
    const userDataRef = useRef(userData);

    // Safety-net sync: any future code path that calls setPendingDraft
    // without manually updating the ref still gets eventually-consistent
    // ref state via this effect.
    useEffect(() => {
        pendingDraftRef.current = pendingDraft;
    }, [pendingDraft]);

    useEffect(() => {
        userDataRef.current = userData;
    }, [userData]);

    // Workspace-exit cleanup: when the user navigates from a workspace
    // view (workspace / build-artifacts / logic-chain) to anything else,
    // clear all draft + prefill state so a stale heading or auto-send
    // can't leak into the next view.
    const prevMainViewRef = useRef(routeView);
    useEffect(() => {
        const isWorkspaceView = (v: string): boolean =>
            v === 'workspace' || v === 'build-artifacts' || v === 'logic-chain';
        const wasWorkspace = isWorkspaceView(prevMainViewRef.current);
        const isNowWorkspace = isWorkspaceView(routeView);
        if (wasWorkspace && !isNowWorkspace) {
            draftGenerationRef.current += 1;
            pendingDraftRef.current = null;
            setPendingDraft(null);
            setDraftCreateError(null);
            setDraftRetryAttempt(0);
            setPrefillChatInput(null);
            setAutoSendPrefill(false);
        }
        prevMainViewRef.current = routeView;
    }, [routeView, setPrefillChatInput, setAutoSendPrefill]);

    // Project-switch cleanup: when activeProject changes from one real
    // project to a different real project (e.g. user opened B from the
    // library while in workspace view), clear stale prefill / draft so
    // it doesn't leak. null transitions are intentional draft-mode
    // entries (see begin*Draft) and lazy-create completions; both are
    // skipped here.
    const prevActiveProjectIdRef = useRef<string | null>(null);
    useEffect(() => {
        const newId = activeProjectId ?? null;
        if (
            prevActiveProjectIdRef.current !== null
            && newId !== null
            && prevActiveProjectIdRef.current !== newId
        ) {
            draftGenerationRef.current += 1;
            pendingDraftRef.current = null;
            setPendingDraft(null);
            setDraftCreateError(null);
            setDraftRetryAttempt(0);
            setPrefillChatInput(null);
            setAutoSendPrefill(false);
        }
        prevActiveProjectIdRef.current = newId;
    }, [activeProjectId, setPrefillChatInput, setAutoSendPrefill]);

    // Shared entry: stash the draft, clear any live project, reset prefill +
    // error state, then route to the matching empty canvas. `beginBlankDraft`
    // (sidebar) and `beginDraft` (Build box) are thin wrappers over this.
    const enterDraft = useCallback(async (draft: WorkspaceDraft): Promise<void> => {
        // Clear even when no project is active. That also invalidates a lazy
        // create request from an abandoned draft, so its late response cannot
        // activate the wrong project.
        if (!(await clearActiveProject())) {
            return;
        }
        // Clear the previous project's chat. Clearing activeProject alone isn't
        // enough: chatHistory falls back to the active chat when there's no
        // project, and activeChatId still points at the old project's chat —
        // so its messages would leak into the blank draft. Reset with no
        // projectId to mint a fresh empty chat. (The lazy-create later calls
        // resetChatState(project_id) again once a real project exists.)
        resetChatState();
        draftGenerationRef.current += 1;
        pendingDraftRef.current = draft;
        setPendingDraft(draft);
        setPrefillChatInput(null);
        setAutoSendPrefill(false);
        setDraftCreateError(null);
        setDraftRetryAttempt(0);
        if (draft.type === 'build') {
            navigateToView('build-artifacts');
            setWorkspaceMode('plan');
        } else {
            navigateToView('workspace');
            setWorkspaceMode('research');
        }
    }, [
        clearActiveProject,
        navigateToView,
        setWorkspaceMode,
        setPrefillChatInput,
        setAutoSendPrefill,
        resetChatState,
    ]);

    const beginBlankDraft = useCallback(async (type: DraftType): Promise<void> => {
        await enterDraft({ type, topic: null });
    }, [enterDraft]);

    const beginDraft = useCallback(async (topic: TopicGroup): Promise<void> => {
        await enterDraft({ type: 'build', topic });
    }, [enterDraft]);

    const trySendDraft = useCallback((msg: string): { handled: boolean } => {
        const liveActiveProject = useProjectStore.getState().activeProject;
        const liveDraft = pendingDraftRef.current;
        if (liveActiveProject || !liveDraft) {
            return { handled: false };
        }
        if (draftCreateInFlightRef.current) {
            // Already creating a project; treat as handled so the caller
            // doesn't double-send into the default path.
            return { handled: true };
        }
        const userId = userDataRef.current?.email;
        if (!userId) {
            console.error('[useWorkspaceDraftMode] Lazy-create blocked: missing userData.email.');
            setDraftCreateError("Can't create the project: user not loaded. Refresh the page and try again.");
            return { handled: true };
        }
        const draftType = liveDraft.type;
        draftCreateInFlightRef.current = true;
        const generation = draftGenerationRef.current;
        const projectName = deriveProjectName(msg);
        // Description = topic title (e.g. "Quantitative models") when seeded
        // from a Build box, else empty (sidebar blank drafts). NOT the raw
        // chat message — keeps admin / audit exports readable.
        const description = liveDraft.topic?.title ?? '';
        // Pre-arm prefill+autoSend BEFORE calling createProject. Setter
        // ordering matters: GeminiChatArea reads `initialInput` via
        // useState(initialInput) AT MOUNT, and the store's createProject
        // sets activeProject synchronously when the API resolves —
        // changing the GeminiChatArea key from `draft-N` to the new
        // project_id and triggering the remount that auto-sends. If we
        // armed prefill in `.then`, the remount would happen first with
        // prefill=null and miss the mount-time snapshot.
        //
        // Invariant: between this pre-arm and API resolve, the
        // GeminiChatArea key is stable (activeProject is null,
        // draftRetryAttempt doesn't change), so no spurious remount can
        // fire the auto-send mid-flight.
        setPrefillChatInput(msg);
        setAutoSendPrefill(true);
        setDraftCreateError(null);

        // skipReset is conditional on the live activeProject being null.
        // enterDraft already clears it, but a websocket / collaboration
        // event between draft entry and first-send could re-set it.
        // Falling back to skipReset:false in that case lets the store
        // do its full reset (idempotent for our flow but defensive).
        const useSkipReset = useProjectStore.getState().activeProject === null;

        void useProjectStore.getState()
            .createProject(userId, projectName, description, draftType, { skipReset: useSkipReset })
            .then((project) => {
                draftCreateInFlightRef.current = false;
                const isStale = draftGenerationRef.current !== generation;
                if (!project) {
                    console.error(
                        `[useWorkspaceDraftMode] Lazy-create returned no project${isStale ? ' (stale, suppressed)' : ''}.`,
                    );
                    // autoSendPrefill is cleared regardless of staleness:
                    // it's GLOBAL state that any GeminiChatArea remount can
                    // consume. Leaving it armed could fire an unintended
                    // auto-send into whatever view the user navigated to.
                    setAutoSendPrefill(false);
                    if (!isStale) {
                        // Keep prefillChatInput=msg so the forced-remount
                        // (via draftRetryAttempt key bump) restores the
                        // user's typed message in the composer textarea.
                        setDraftCreateError("Couldn't create the project. Press send again to retry.");
                        setDraftRetryAttempt((n) => n + 1);
                    }
                    return;
                }
                // Stale-check BEFORE resetChatState: an abandoned draft's
                // project shouldn't get its chat-loading state poked even
                // if it's harmless. Cleaner semantics for stale paths.
                if (isStale) return;
                resetChatState(project.project_id);
                // Sync the ref BEFORE the React setter so a concurrent
                // send sees the cleared draft synchronously (closes the
                // inFlightRef-vs-state race window).
                pendingDraftRef.current = null;
                setPendingDraft(null);
                setDraftCreateError(null);
                setDraftRetryAttempt(0);
                // Smart project title (BAR-320 follow-up): rename from the topic
                // of the first message instead of the truncated raw text.
                // Fire-and-forget — placeholder name shows until this resolves.
                void useProjectStore.getState().autoTitleProject(project.project_id, userId, msg, projectName);
                // BAR-40: the draft just created a real project (now active in
                // the store), so move the URL off the /…/new sentinel to the
                // project path. navigateToView resolves the id from the active
                // project — so a refresh now restores the project instead of
                // dropping the user back into a blank draft.
                navigateToView(draftType === 'build' ? 'build-artifacts' : 'workspace');
            })
            .catch((err: unknown) => {
                draftCreateInFlightRef.current = false;
                const isStale = draftGenerationRef.current !== generation;
                console.error(
                    `[useWorkspaceDraftMode] Lazy-create error${isStale ? ' (stale, suppressed)' : ''}:`,
                    err,
                );
                setAutoSendPrefill(false);
                if (!isStale) {
                    const message = err instanceof Error && err.message
                        ? `Couldn't create the project: ${err.message}. Press send again to retry.`
                        : "Couldn't create the project. Check your connection and press send again to retry.";
                    setDraftCreateError(message);
                    setDraftRetryAttempt((n) => n + 1);
                }
            });

        return { handled: true };
    }, [navigateToView, setPrefillChatInput, setAutoSendPrefill, resetChatState]);

    return {
        pendingDraft,
        draftCreateError,
        draftRetryAttempt,
        beginBlankDraft,
        beginDraft,
        trySendDraft,
    };
}
